import React from 'react'
import { TAsset } from '../../../../types'
import * as THREE from 'three'
import AssetEditorProps, { TAssetEditorProp, TAssetEditorSection } from '../assetEditorProps'
import { AMAMode, EditorConstructor, useMaterialState, utilsEditor, utilsMaterial } from '@amaspace-editor/editor-3d'
import { useGetAssetByIdQuery, useUpdateAssetMutation } from '../../../../api/assetApi'
import toast from 'react-hot-toast'
import { AMATextureTypes } from '@amaspace-editor/editor-3d/lib/types/configInput.types'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import Utils, { objectUtils } from '../../../../utils'

export const MATERIAL_SIDES = {
    front_side: 0,
    back_side: 1,
    double_side: 2,
} as const

export const BLENDING_TYPES = {
    no_blending: 0,
    normal_blending: 1,
    additive_blending: 2,
    subtractive_blending: 3,
    multiply_blending: 4,
    custom_blending: 5
} as const

interface AssetEditorMaterialProps {
    asset?: TAsset
}

const AssetEditorMaterial = ({ asset }: AssetEditorMaterialProps) => {
    const { config } = useMaterialState()
    const editorUtils3D = utilsEditor()
    const materialEdit = utilsMaterial()
    const { '*': splat } = useParams()
    const list = splat?.split('/')
    const id = list?.pop()

    const [updateAssetRequest] = useUpdateAssetMutation()
    const fetchAssetByIdResponse = useGetAssetByIdQuery({ id: id as string })

    const materialProps = (): TAssetEditorSection[] => {
        if (!config) return []
        const props = config.properties
        const textures = config.textures
        const side = props.doubleside ? THREE.DoubleSide : props.side

        const currentMap = (map: keyof AMATextureTypes) => textures.find(x => x.type === map)

        return [
            {
                name: 'Material',
                props: [
                    { type: 'input', label: 'Name', value: props?.name, key: 'name_prop' },
                    {
                        type: 'range',
                        label: 'Metalness',
                        value: props.metalness,
                        key: 'metalness_prop',
                        misc: { max: 1 }
                    },
                    {
                        type: 'range',
                        label: 'Roughness',
                        value: props.roughness,
                        key: 'roughness_prop',
                        misc: { max: 1 }
                    },
                    { type: 'range', label: 'Opacity', value: props.alpha, key: 'alpha_prop', misc: { max: 1 } },
                    { type: 'attachTexture', label: 'Map', value: currentMap('map'), key: 'map' },
                    { type: 'attachTexture', label: 'Normal Map', value: currentMap('normalMap'), key: 'normal_map' },
                    { type: 'attachTexture', label: 'Alpha Map', value: currentMap('alphaMap'), key: 'alpha_map' },
                    {
                        type: 'attachTexture',
                        label: 'Roughness Map',
                        value: currentMap('roughnessMap'),
                        key: 'roughness_map'
                    },
                    {
                        type: 'attachTexture',
                        label: 'Metalness Map',
                        value: currentMap('metalnessMap'),
                        key: 'metalness_map'
                    },
                    {
                        type: 'attachTexture',
                        label: 'Emissive Map',
                        value: currentMap('emissiveMap'),
                        key: 'emissive_map'
                    },
                    { type: 'attachTexture', label: 'AO Map', value: currentMap('aoMap'), key: 'ao_map' },
                    { type: 'color', label: 'Color', value: props.color, key: 'color_prop' },
                    { type: 'color', label: 'Emissive Color', value: props.emissive, key: 'emissive_prop' },
                    { type: 'switch', label: 'Depth Write', value: props.depthWrite, key: 'depthWrite_prop' },
                    { type: 'switch', label: 'Depth Test', value: props.depthTest, key: 'depthTest_prop' },
                    { type: 'switch', label: 'Wireframe', value: props.wireframe, key: 'wireframe_prop' },
                    { type: 'switch', label: 'Transparent', value: props.transparent, key: 'transparent_prop' },
                    {
                        type: 'list',
                        label: 'Side',
                        value: objectUtils({ obj: MATERIAL_SIDES, value: side }).key,
                        key: 'side',
                        misc: {
                            list: [
                                { label: 'front', value: THREE.FrontSide, key: 'front_side' },
                                { label: 'back', value: THREE.BackSide, key: 'back_side' },
                                { label: 'double', value: THREE.DoubleSide, key: 'double_side' }
                            ]
                        }
                    },
                    // {
                    //   type: 'list',
                    //   label: 'Blending',
                    //   value: objectUtils<typeof BLENDING_TYPES>({ obj: BLENDING_TYPES, value: props.blending }).key,
                    //   key: 'blending',
                    //   misc: {
                    //     list: [
                    //       { label: 'No', value: THREE.NoBlending, key: 'no_blending' },
                    //       { label: 'Normal', value: THREE.NormalBlending, key: 'normal_blending' },
                    //       { label: 'Multiply', value: THREE.MultiplyBlending, key: 'multiply_blending' },
                    //       { label: 'Subtractive', value: THREE.SubtractiveBlending, key: 'subtractive_blending' },
                    //       { label: 'Additive', value: THREE.AdditiveBlending, key: 'additive_blending' },
                    //       { label: 'Custom', value: THREE.CustomBlending, key: 'custom_blending' },
                    //     ]
                    //   }
                    // },
                    { type: 'button', label: 'Update', value: 'Save', key: 'update' }
                    // { type: 'button', label: 'Reset', value: 'Reset', key: 'reset' },
                ]
            }
        ]
    }

    const changeProps = async (key: string, value: TAssetEditorProp['value']) => {
        const keyProp = key.split('_').slice(-1)[0]
        const keyValue = key.split('_')[0]

        if (keyProp === 'prop') {
            materialEdit.updateMaterialProps({ [keyValue]: value })
        }
        if (keyProp === 'map') {
            materialEdit.updateMaterialTextures(value)
        }
        if (keyProp === 'side') {
            const sideValue = objectUtils<typeof MATERIAL_SIDES>({ obj: MATERIAL_SIDES, key: value })
                .value as THREE.Side
            materialEdit.updateMaterialProps({ side: sideValue, doubleside: false })
        }
        if (keyProp === 'blending') {
            const blendingValue = objectUtils({ obj: BLENDING_TYPES, key: value }).value as THREE.Blending
            materialEdit.updateMaterialProps({ blending: blendingValue })
        }

        if (key === 'update') {
            // const preview = Utils.generateAndUploadThumbnail(
            //     asset?.name || '',
            //     editorUtils3D.threeState?.gl.domElement.toDataURL() || '',
            //     true
            // )
            // await toast.promise(preview, {
            //     loading: 'Updating preview...',
            //     success: 'Preview updated!',
            //     error: 'Error when updating preview!'
            // })

            const assetData = {
                id: asset!.id,
                asset: {
                    ...asset,
                    patch: {
                        ...asset?.patch,
                        updatedProps: {
                            ...config.properties
                        },
                        updatedTextures: [...config.textures]
                    }
                    // preview: await preview
                }
            }
            toast.promise(updateAssetRequest(assetData as any), {
                loading: 'Updating...',
                success: 'Update complete!',
                error: 'Update failed!'
            })
        }
        if (key === 'reset') {
            const assetData = {
                id: asset!.id,
                asset: {
                    ...asset,
                    patch: {
                        ...asset?.patch,
                        updatedProps: {},
                        updatedTextures: []
                    }
                }
            }
            toast.promise(updateAssetRequest(assetData as any), {
                loading: 'Updating...',
                success: 'Update complete!',
                error: 'Update failed!'
            })
        }
    }
    const renderMaterial = () => {
        const data = structuredClone(fetchAssetByIdResponse.data)
        return (
            <MaterialWrapper>
                <EditorConstructor
                    type={asset!.type as 'model' | 'material'}
                    data={data}
                    presetConfig={{ mode: AMAMode.EDITOR }}
                />
            </MaterialWrapper>
        )
    }
    return (
        <>
            <Wrapper>
                {renderMaterial()}
                <PropsWrapper>
                    <AssetEditorProps asset={asset} sectionsList={materialProps()} onPropChange={changeProps} />
                </PropsWrapper>
            </Wrapper>
        </>
    )
}

export default AssetEditorMaterial

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 40px;
    height: calc(100vh - 300px);
    width: 100%;
`

const MaterialWrapper = styled.div`
    height: 100%;
    flex: 1;
`
const PropsWrapper = styled.div`
    flex: 0.5;
`
